// Запросы панели «Администрирование» (web/server/stoweb/routes/admin.py, только роль admin).
import { api } from '../../core/api';
import type { ViewItem, ViewTable } from '../auth/referrals';

const enc = encodeURIComponent;

export interface AUser {
  id: number; login: string; name: string; role: 'admin' | 'user'; blocked: boolean; mustChangePassword: boolean;
  created: number; lastLogin: number | null; deleted: number | null; quotaBytes: number | null; quotaCustom: boolean;
  maxProjects: number | null; usedBytes: number; projects: number; sessions: number; purgeAt: number | null;
  subscription: { active: boolean; lifetime: boolean; until: number | null; exempt: boolean };
  telegram: { linked: boolean; username: string }; referrer: string;
}
export interface ARefLabels {
  intro: string; empty: string; details: string; settle: string; amount: string; note: string; notePlaceholder: string; save: string;
  done: string; cancel: string; close: string; remove: string; removeAsk: string; removeOk: string; removed: string;
}
export interface ARefField { key: string; label: string; hint: string; unit: string; value: number }
export interface ACode {
  id: number; code: string; days: number; lifetime: boolean; maxUses: number; uses: number; expires: number | null; created: number;
  revoked: boolean; note: string; source: string; active: boolean; paidRub: number; usedBy?: { login: string; ts: number }[];
}
export interface AInvite {
  id: number; code: string; path: string; created: number; expires: number | null; maxUses: number; uses: number;
  revoked: boolean; note: string; role: 'admin' | 'user'; active: boolean; subDays: number;
}
export interface AProject {
  id: string; name: string; owner: { id: number; login: string; name: string }; title: string; type: string;
  discipline: string; student: string; modified: string; size: number; mtime: number; trashed?: string; error?: string | null;
  byAdmin?: boolean; trashedBy?: string;
}
export interface AAudit { id: number; ts: number; uid: number | null; login: string; ip: string; action: string; target: string; details: any }
export interface ASettings {
  registration: 'invite' | 'open' | 'closed'; quota_mb: number; max_file_mb: number; max_projects: number; session_hours: number;
  remember_days: number; public_urls: string[]; queue_wait_min: number; max_jobs_per_user: number; user_trash_days: number;
  word_max_min: number; new_user_sub_days: number; price_1m: number; price_3m: number; price_6m: number; price_12m: number;
  stars_1m: number; stars_3m: number; stars_6m: number; stars_12m: number; tg_bot: string;
}
export interface AServer {
  host: string; port: number; https: boolean; trustProxy: boolean; localOnly: boolean; publicUrls: string[];
  settingsPublicUrls: string[]; dataDir: string; warnings: string[]; word: boolean; time: number;
}
export interface AJob {
  id: string; kind: string; project: string; owner: number | null; ownerLogin: string; status: string; progress: string;
  queue: number | null; created: number; elapsed: number; error: string | null; cancelRequested: boolean;
  cancelledBy?: { login: string; admin: boolean } | null; wordSeconds?: number | null;
}
export interface AStorage {
  users: { id: number; login: string; role: string; deleted: boolean; usedBytes: number; trashBytes: number; quotaBytes: number | null;
    projects: number; maxProjects: number | null }[];
  totalBytes: number; disk: { total: number; used: number; free: number } | null; dbBytes: number;
}

export const adminApi = {
  users: (deleted = false) => api.get<{ users: AUser[]; owner: number | null }>('/api/admin/users' + (deleted ? '?deleted=1' : '')),
  createUser: (b: { login: string; name?: string; role?: string; password?: string; mustChange?: boolean }) =>
    api.post<{ user: AUser; tempPassword?: string }>('/api/admin/users', b),
  updateUser: (uid: number, b: Partial<{ role: string; blocked: boolean; name: string; quota_mb: number | null; max_projects: number | null }>) =>
    api.patch<{ user: AUser }>(`/api/admin/users/${uid}`, b),
  resetPassword: (uid: number) => api.post<{ tempPassword: string; user: AUser }>(`/api/admin/users/${uid}/reset-password`, {}),
  revokeSessions: (uid: number) => api.post<{ revoked: number }>(`/api/admin/users/${uid}/revoke-sessions`, {}),
  deleteUser: (uid: number, confirm: string) => api.del<{ deleted: boolean; restoreDays: number }>(`/api/admin/users/${uid}`, { confirm }),
  restoreUser: (uid: number) => api.post<{ user: AUser }>(`/api/admin/users/${uid}/restore`, {}),
  purgeUser: (uid: number, confirm: string) => api.post<{ purged: boolean }>(`/api/admin/users/${uid}/purge`, { confirm }),

  invites: () => api.get<{ invites: AInvite[] }>('/api/admin/invites'),
  createInvite: (b: { hours?: number; maxUses?: number; note?: string; role?: string; subDays?: number }) => api.post<AInvite>('/api/admin/invites', b),
  revokeInvite: (id: number) => api.del<{ revoked: boolean }>(`/api/admin/invites/${id}`),

  subscription: (uid: number, b: { action: 'extend' | 'until' | 'lifetime' | 'cancel'; days?: number; months?: number; date?: string; paidRub?: number }) =>
    api.post<{ user: AUser }>(`/api/admin/users/${uid}/subscription`, b),
  setReferrer: (uid: number, login: string) => api.post<{ user: AUser }>(`/api/admin/users/${uid}/referrer`, { login }),
  referrals: () => api.get<{ labels: ARefLabels; fields: ARefField[]; summary: ViewItem[]; table: ViewTable }>('/api/admin/referrals'),
  referralDetail: (uid: number) => api.get<{ login: string; summary: ViewItem[]; tables: ViewTable[] }>(`/api/admin/referrals/${uid}`),
  settle: (uid: number, b: { amount: string; note: string }) => api.post<{ ok: boolean }>(`/api/admin/referrals/${uid}/settle`, b),
  unsettle: (id: number) => api.del<{ ok: boolean }>(`/api/admin/referrals/settle/${id}`),
  codes: () => api.get<{ codes: ACode[] }>('/api/admin/codes'),
  createCodes: (b: { days: number; maxUses: number; validDays: number | null; note: string; count: number; paidRub: number }) => api.post<{ codes: ACode[] }>('/api/admin/codes', b),
  revokeCode: (id: number) => api.del<{ revoked: boolean }>(`/api/admin/codes/${id}`),

  projects: (p: { q?: string; owner?: number; type?: string; trash?: boolean }) => {
    const qs = new URLSearchParams();
    if (p.q) qs.set('q', p.q);
    if (p.owner) qs.set('owner', String(p.owner));
    if (p.type) qs.set('type', p.type);
    if (p.trash) qs.set('trash', '1');
    return api.get<{ projects: AProject[] }>('/api/admin/projects?' + qs.toString());
  },
  trashProject: (id: string) => api.del<{ trashed: string }>(`/api/projects/${enc(id)}`),
  restoreProject: (owner: number, id: string) => api.post<{ id: string }>('/api/admin/projects/trash/restore', { owner, id }),
  purgeProject: (owner: number, ids: string[]) => api.post<{ purged: string[] }>('/api/admin/projects/trash/purge', { owner, ids }),
  exportZip: (id: string) => api.post<{ file?: string }>(`/api/projects/${enc(id)}/export`, { format: 'zip' }),
  fileUrl: (id: string, name: string) => `/api/projects/${enc(id)}/files/${name.split('/').map(enc).join('/')}`,

  audit: (p: { action?: string; login?: string; q?: string; since?: number; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== '' && v !== 0) qs.set(k, String(v));
    return api.get<{ items: AAudit[]; total: number }>('/api/admin/audit?' + qs.toString());
  },
  settings: () => api.get<{ settings: ASettings; server: AServer }>('/api/admin/settings'),
  saveSettings: (b: Partial<ASettings>) => api.put<{ settings: ASettings; changed: Record<string, unknown>; server: AServer }>('/api/admin/settings', b),
  queue: () => api.get<{ jobs: AJob[]; word: { busy: boolean; queue: string[]; maxRunMin?: number;
    holder?: { id: string; ownerLogin: string; kind: string; project: string; seconds: number } | null } }>('/api/admin/queue'),
  cancelJob: (id: string) => api.post<{ cancelled: boolean; status: string }>(`/api/admin/queue/${enc(id)}/cancel`, {}),
  storage: () => api.get<AStorage>('/api/admin/storage'),
  server: () => api.get<AServer>('/api/admin/server'),
};
